import {
  DatepickerSimple,
  Select,
  Timestamp,
  BankFileConversionRail,
  FileConversionConfiguration,
  objectEntries,
  FileConversionService,
  InteracWordmark,
  FieldData,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { format } from 'date-fns';
import { ReactNode } from 'react';

export type ProcessFileFormValues = {
  Service: FileConversionService;
  PaymentRail: BankFileConversionRail | '';
  FileConversionConfigurationID: string;
  ValueDate: string;
  AlternatePaymentRail: BankFileConversionRail | '';
};

export const useprocessFileFormFields = ({
  configurations,
  configurationsLoading,
}: {
  configurations: FileConversionConfiguration[];
  configurationsLoading: boolean;
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.FileUploadDrawer.ConvertTemplate' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const getRailNames = (): Record<BankFileConversionRail, ReactNode> => ({
    eft: tGlobal('PaymentRail.eft'),
    vopayinstant: tGlobal('PaymentRail.vopayinstant'),
    ach: tGlobal('PaymentRail.ach'),
    rtp: tGlobal('PaymentRail.rtp'),
    fednow: tGlobal('PaymentRail.fednow'),
    fedwire: tGlobal('PaymentRail.wire'),
    interac: <InteracWordmark />,
    aft: tGlobal('PaymentRail.aft'),
    creditcard: tGlobal('PaymentRail.creditcard'),
    debitcard: tGlobal('PaymentRail.debitcard'),
    oct: tGlobal('PaymentRail.oct'),
  });

  const getValidServiceKey = (service: string) => {
    const serviceKey = service.toLowerCase();
    return serviceKey === 'credit' || serviceKey === 'debit' ? serviceKey : undefined;
  };

  const getBankOptions = (service: string) => {
    const validServiceKey = getValidServiceKey(service);

    return configurations
      .filter((config) => {
        if (!validServiceKey) return false;
        const railsObj = config.PaymentRails?.[validServiceKey];
        return railsObj && typeof railsObj === 'object' && Object.keys(railsObj).length > 0;
      })
      .map((config) => ({
        value: config.FileConversionConfigurationID,
        label: config.AccountNumber
          ? `${config.FinancialInstitution} - ${config.AccountNumber}`
          : config.FinancialInstitution,
      }));
  };

  const getProductOptions = (conversionID: string, service: string) => {
    const bankConfig = configurations.find((config) => config.FileConversionConfigurationID === conversionID);
    if (!bankConfig) return [];

    const validServiceKey = getValidServiceKey(service);
    const railsObj = validServiceKey ? bankConfig.PaymentRails?.[validServiceKey] : undefined;
    if (!railsObj || typeof railsObj !== 'object') return [];

    const railNames = getRailNames();

    return objectEntries(railsObj).map(([rail, value]) => ({
      isDisabled: !value.SubmissionEnabled,
      value: rail,
      label: railNames[rail],
    }));
  };

  const convertTo: FieldData<ProcessFileFormValues>[] = [
    {
      fieldName: 'FileConversionConfigurationID',
      label: () => t('processorLabel', { defaultValue: 'Processor' }),
      getInputState: () => 'optional',
      showField: () => {
        // Hide if only default processor is available
        if (configurations.length === 1 && configurations[0].Processor === 'default_processor') {
          return false;
        }
        return true;
      },
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        const options = getBankOptions(ctx.values.Service);
        const formikData = getFormikData<ProcessFileFormValues['FileConversionConfigurationID']>({
          onChange: (event) => {
            const selectedValue = event.target?.value || '';
            const bankConfig = configurations.find((config) => config.FileConversionConfigurationID === selectedValue);

            const resetFields = () => {
              ctx.setFieldValue('PaymentRail', '');
              ctx.setFieldTouched('PaymentRail', false);
              ctx.setFieldValue('AlternatePaymentRail', '');
            };

            if (!bankConfig) {
              resetFields();
              return;
            }

            const validServiceKey = getValidServiceKey(ctx.values.Service);
            const railsObj = validServiceKey ? bankConfig.PaymentRails?.[validServiceKey] : undefined;

            if (!railsObj || typeof railsObj !== 'object') {
              resetFields();
              return;
            }

            const defaultRail = objectEntries(railsObj).find(
              ([_, config]) => config.IsDefault && config.SubmissionEnabled,
            );

            if (defaultRail) {
              ctx.setFieldValue('PaymentRail', defaultRail[0]);
            } else {
              resetFields();
            }
          },
        });

        return (
          <Select
            version="2"
            {...formikData}
            options={options}
            placeholder=""
            sort={false}
            loading={configurationsLoading}
          />
        );
      },
    },
    {
      fieldName: 'PaymentRail',
      label: () => t('productLabel', { defaultValue: 'Payment Rail' }),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        const productOption = getProductOptions(ctx.values.FileConversionConfigurationID, ctx.values.Service);
        const formikData = getFormikData<ProcessFileFormValues['PaymentRail']>({
          onChange: (event) => {
            const selectedValue = event.target?.value || '';
            const availableRails = productOption.filter(
              (option) => !option.isDisabled && option.value !== selectedValue,
            );
            ctx.setFieldValue('AlternatePaymentRail', availableRails.length > 0 ? availableRails[0].value : '');
          },
        });

        return (
          <Select
            version="2"
            {...formikData}
            options={productOption}
            loading={configurationsLoading}
            disabled={productOption.length === 0}
            disableOptionWhen={(o) => o.isDisabled}
            placeholder=""
            sort={false}
          />
        );
      },
    },
    {
      fieldName: 'AlternatePaymentRail' as keyof ProcessFileFormValues,
      label: () => t('alternativePaymentRailLabel', { defaultValue: 'Fallback Payment Rail' }),
      getInputState: () => 'optional',
      showField: (values) => {
        // Hide if only default processor is available
        if (configurations.length === 1 && configurations[0].Processor === 'default_processor') {
          return false;
        }

        // Hide if only 1 or fewer rails available
        const productOption = getProductOptions(values.FileConversionConfigurationID, values.Service);
        const enabledRails = productOption.filter((option) => !option.isDisabled);
        return enabledRails.length > 1;
      },
      renderField: ({ getFormikData, ctx }) => {
        const productOption = getProductOptions(ctx.values.FileConversionConfigurationID, ctx.values.Service);

        return (
          <Select
            version="2"
            {...getFormikData<ProcessFileFormValues['AlternatePaymentRail']>({})}
            options={productOption}
            placeholder=""
            disableOptionWhen={(o) => o.isDisabled || o.value === ctx.values.PaymentRail}
            disabled={productOption.length === 0}
            sort={false}
            loading={configurationsLoading}
          />
        );
      },
    },
    {
      fieldName: 'ValueDate',
      label: () => t('valueDateLabel', { defaultValue: 'Value Date' }),
      getInputState: () => 'required',
      serializeField: (values) => {
        const endDate = new Date(Number(values.ValueDate));
        return format(endDate, 'yyyy-MM-dd');
      },
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData }) => {
        const data = getFormikData<ProcessFileFormValues['ValueDate']>({});
        return (
          <DatepickerSimple
            variant="outlineShadow"
            position="topLeft"
            {...data}
            min={new Date().getTime() as Timestamp}
            isDisabled={data.disabled}
          />
        );
      },
    },
  ];

  return { convertTo };
};
